/**
 * Refund Policy — production legal text satisfying Paddle's verification.
 * Covers 15-day free trial, subscription terms, cancellation, refund process,
 * billing dispute path.
 */
import React from "react";
import { LegalPageLayout, LegalSection } from "./LegalPageLayout";

const SECTIONS = [
  { id: "overview", title: "1. Overview" },
  { id: "trial", title: "2. 15-Day Free Trial" },
  { id: "subscriptions", title: "3. Subscription Billing" },
  { id: "renewals", title: "4. Automatic Renewals" },
  { id: "cancellation", title: "5. Cancellation Process" },
  { id: "refund-eligibility", title: "6. Refund Eligibility" },
  { id: "exceptions", title: "7. Statutory Refunds" },
  { id: "how-to-request", title: "8. How to Request a Refund" },
  { id: "processing-time", title: "9. Refund Processing Time" },
  { id: "billing-disputes", title: "10. Billing Disputes" },
  { id: "contact", title: "11. Contact" },
];

export default function RefundPolicyPage() {
  return (
    <LegalPageLayout
      title="Refund Policy"
      subtitle="Our trial, cancellation, and refund terms."
      lastUpdated="May 29, 2026"
      sections={SECTIONS}
    >
      <LegalSection id="overview" title="1. Overview">
        <p>
          This Refund Policy explains when and how you may obtain a refund for
          Ashra AI subscriptions. By subscribing you accept the terms of this
          policy, our <a href="/terms">Terms of Service</a> and our
          <a href="/privacy"> Privacy Policy</a>.
        </p>
        <p>
          All subscriptions are processed by <strong>Paddle.com Market Ltd</strong>
          (“Paddle”) as Merchant of Record. Refunds, when granted, are returned to
          the original payment method used at checkout.
        </p>
      </LegalSection>

      <LegalSection id="trial" title="2. 15-Day Free Trial">
        <p>
          We offer a <strong>15-day free trial</strong> on the IGNITE and ASCEND
          plans. During the trial period:
        </p>
        <ul>
          <li>You are <strong>not charged</strong>.</li>
          <li>You can cancel at any time from <code>Settings → Billing</code> with no further obligation.</li>
          <li>If you do not cancel before the trial ends, your subscription automatically converts to a paid plan on day 16 and the payment method you provided will be charged.</li>
        </ul>
        <p>
          Trial eligibility is limited to one trial per user, household and
          payment method. Abuse of the trial system (multiple sign-ups, fake
          accounts) may result in termination without refund.
        </p>
      </LegalSection>

      <LegalSection id="subscriptions" title="3. Subscription Billing">
        <p>
          Paid plans are billed in advance on a recurring basis: monthly plans
          renew every 30 days, annual plans renew every 12 months. Pricing is
          shown on our <a href="/#pricing">pricing page</a> and may be updated
          subject to the notice provisions in our Terms of Service.
        </p>
        <p>
          Sales tax, VAT, GST and other applicable taxes are calculated and added
          at checkout by Paddle based on your billing country.
        </p>
      </LegalSection>

      <LegalSection id="renewals" title="4. Automatic Renewals">
        <p>
          Subscriptions <strong>automatically renew</strong> at the end of each
          billing cycle for the same plan and term unless cancelled before the
          renewal date. Renewal charges are non-refundable except where required
          by applicable law (see Section 7).
        </p>
        <p>
          Paddle sends a payment receipt for every renewal to the email on file.
          If you do not receive a receipt, please check your spam folder before
          contacting us.
        </p>
      </LegalSection>

      <LegalSection id="cancellation" title="5. Cancellation Process">
        <p>You can cancel your subscription at any time via:</p>
        <ul>
          <li><strong>In-app:</strong> <code>Settings → Billing → Cancel subscription</code>;</li>
          <li><strong>Paddle Customer Portal:</strong> the link is included in every billing email from Paddle.</li>
        </ul>
        <p>
          Cancellation takes effect at the <strong>end of your current paid
          billing cycle</strong>. You continue to have full access until that
          date. We do not pro-rate refunds for unused portions of a billing cycle.
        </p>
      </LegalSection>

      <LegalSection id="refund-eligibility" title="6. Refund Eligibility">
        <p>
          We operate on a <strong>no-refund policy</strong> for subscription
          payments, with the following limited exceptions:
        </p>
        <ul>
          <li><strong>Duplicate charge:</strong> if you were charged twice for the same billing period due to a technical error on our side or Paddle's, we will refund the duplicate charge in full.</li>
          <li><strong>Fraudulent charge:</strong> if your payment method was used without your authorization, contact us and Paddle immediately. We will cooperate with your card issuer's investigation.</li>
          <li><strong>Service unavailability:</strong> if the Service is materially unavailable for more than 72 consecutive hours due to our fault, we may at our discretion offer a pro-rated refund or service credit.</li>
          <li><strong>Statutory rights:</strong> see Section 7 for refunds we are required to provide under applicable consumer-protection laws.</li>
        </ul>
        <p>
          Refunds are not granted for:
        </p>
        <ul>
          <li>change of mind after a renewal has processed;</li>
          <li>forgetting to cancel before the trial ended;</li>
          <li>partial use of a billing period;</li>
          <li>failure to use the Service during a paid period;</li>
          <li>account termination resulting from your breach of our <a href="/terms">Terms</a>.</li>
        </ul>
      </LegalSection>

      <LegalSection id="exceptions" title="7. Statutory Refunds">
        <p>
          <strong>European Union / United Kingdom (Right of Withdrawal):</strong>
          Consumers in the EU and UK have a 14-day right of withdrawal for online
          services. By starting the Service immediately and using it before the
          14-day period expires, you expressly waive this right of withdrawal in
          accordance with applicable law (EU Directive 2011/83/EU, art. 16(m); UK
          Consumer Contracts Regulations 2013, reg. 36).
        </p>
        <p>
          You may still request a refund within the 14-day window <strong>if you
          have not actively used</strong> the Service. Activity is defined as any
          login, AI generation, contact creation, funnel publication, or other
          chargeable action.
        </p>
        <p>
          <strong>Other jurisdictions:</strong> where local consumer-protection
          law grants you a non-waivable refund right, we will honor that right to
          the extent required. Please contact us with details and we will work
          with you in good faith.
        </p>
      </LegalSection>

      <LegalSection id="how-to-request" title="8. How to Request a Refund">
        <p>To request a refund, email
          <a href="mailto:billing@ashraai.com"> billing@ashraai.com</a> from the
          email address on your Ashra AI account. Please include:
        </p>
        <ul>
          <li>your full name and account email;</li>
          <li>the Paddle invoice or order number (from your billing email);</li>
          <li>the date and amount of the charge;</li>
          <li>the reason for your refund request;</li>
          <li>any supporting screenshots or evidence (for duplicate or unauthorized charges).</li>
        </ul>
        <p>
          We respond to refund requests within 5 business days.
        </p>
      </LegalSection>

      <LegalSection id="processing-time" title="9. Refund Processing Time">
        <p>
          Approved refunds are issued to your original payment method through
          Paddle. Processing typically takes:
        </p>
        <ul>
          <li><strong>Credit / debit cards:</strong> 5–10 business days to appear on your statement;</li>
          <li><strong>PayPal / digital wallets:</strong> 1–3 business days;</li>
          <li><strong>Bank transfers / SEPA:</strong> 7–14 business days.</li>
        </ul>
        <p>
          Exact timing depends on your bank or card issuer and is outside our
          control.
        </p>
      </LegalSection>

      <LegalSection id="billing-disputes" title="10. Billing Disputes">
        <p>
          If you believe you have been charged in error, please contact us before
          filing a chargeback with your card issuer. Most issues can be resolved
          within one business day by emailing
          <a href="mailto:billing@ashraai.com"> billing@ashraai.com</a>.
        </p>
        <p>
          Chargebacks filed without first contacting us may result in immediate
          account suspension and recovery of associated processing fees.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="11. Contact">
        <ul>
          <li><strong>Billing:</strong> <a href="mailto:billing@ashraai.com">billing@ashraai.com</a></li>
          <li><strong>Support:</strong> <a href="mailto:support@ashraai.com">support@ashraai.com</a></li>
          <li><strong>Paddle (Merchant of Record):</strong> via the customer portal link in your billing emails.</li>
        </ul>
      </LegalSection>
    </LegalPageLayout>
  );
}
